import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/contexts/LanguageContext";
import {
    Book, Sparkles, Video, Image as ImageIcon, MessageSquare, Stamp, UserPlus,
    Music, Disc3, Wand2, Eraser, Crown, Layers, Bot, Youtube, Mic, Users,
    ArrowRight, Coins, CheckCircle2, ExternalLink
} from "lucide-react";

interface ServiceGuide {
    id: string;
    titleTh: string;
    titleEn: string;
    descriptionTh: string;
    descriptionEn: string;
    icon: React.ReactNode;
    link: string;
    color: string;
    credits: string;
    stepsTh: string[];
    stepsEn: string[];
    tipsTh?: string[];
    tipsEn?: string[];
}

const serviceGuides: ServiceGuide[] = [
    {
        id: "video",
        titleTh: "Veo 3.1 - สร้างวิดีโอ AI",
        titleEn: "Veo 3.1 - AI Video Generation",
        descriptionTh: "สร้างวิดีโอ 8 วินาทีจากข้อความหรือรูปภาพด้วย Google Veo 3.1",
        descriptionEn: "Generate 8-second videos from text or images using Google Veo 3.1",
        icon: <Video className="h-6 w-6" />,
        link: "/generate/video",
        color: "from-cyan-500 to-blue-500",
        credits: "60",
        stepsTh: [
            "เลือกโหมด: Text to Video หรือ Image to Video",
            "พิมพ์คำอธิบาย (Prompt) ที่ต้องการสร้าง",
            "เลือกอัตราส่วน (16:9, 9:16, 1:1)",
            "กดปุ่ม 'สร้างวิดีโอ' และรอผลลัพธ์"
        ],
        stepsEn: [
            "Select mode: Text to Video or Image to Video",
            "Type the description (Prompt) you want to create",
            "Choose aspect ratio (16:9, 9:16, 1:1)",
            "Click 'Generate Video' and wait for results"
        ],
        tipsTh: ["ใช้ Prompt ภาษาอังกฤษจะได้ผลลัพธ์ดีกว่า", "อธิบายฉากให้ละเอียด เช่น แสง สี บรรยากาศ"],
        tipsEn: ["English prompts give better results", "Describe the scene in detail: lighting, colors, atmosphere"]
    },
    {
        id: "seedance",
        titleTh: "Seedance 2.0 - วิดีโอสมจริง",
        titleEn: "Seedance 2.0 - Realistic Video",
        descriptionTh: "สร้างวิดีโอสมจริงสูงด้วย Seedance 2.0 Fast ความยาว 10-15 วินาที",
        descriptionEn: "Create highly realistic videos with Seedance 2.0 Fast, 10-15 seconds long",
        icon: <Sparkles className="h-6 w-6" />,
        link: "/generate/sora/text-to-video",
        color: "from-purple-500 to-pink-500",
        credits: "330-495",
        stepsTh: [
            "เลือกประเภท: Text to Video, Image to Video หรือ Blend",
            "พิมพ์ Prompt หรืออัพโหลดรูปภาพ",
            "เลือกความยาว: 10 หรือ 15 วินาที",
            "กดสร้างและรอผลลัพธ์ (อาจใช้เวลา 2-5 นาที)"
        ],
        stepsEn: [
            "Select type: Text to Video, Image to Video or Blend",
            "Type Prompt or upload images",
            "Choose duration: 10 or 15 seconds",
            "Click generate and wait (may take 2-5 minutes)"
        ],
        tipsTh: ["Sora เก่งเรื่องการเคลื่อนไหวของคน", "วิดีโอจะมี Watermark ของ OpenAI สามารถลบได้ที่หน้า Remove Watermark"],
        tipsEn: ["Sora excels at human movement", "Videos have OpenAI watermark, can be removed at Remove Watermark page"]
    },
    {
        id: "music",
        titleTh: "สร้างเพลง AI",
        titleEn: "AI Music Generation",
        descriptionTh: "สร้างเพลงด้วย Suno V6 รองรับเพลงมีเนื้อร้องและ Instrumental",
        descriptionEn: "Generate music with Suno V6, supports vocals and instrumental",
        icon: <Music className="h-6 w-6" />,
        link: "/generate/music",
        color: "from-green-500 to-emerald-500",
        credits: "12",
        stepsTh: [
            "เลือกโหมด: Custom (กำหนดเอง) หรือ Description (อธิบาย)",
            "กรอกรายละเอียดเพลง หรือเนื้อร้อง",
            "เลือกแนวเพลง (Pop, Rock, Jazz, etc.)",
            "กดสร้างและรอรับเพลงของคุณ"
        ],
        stepsEn: [
            "Select mode: Custom or Description",
            "Enter song details or lyrics",
            "Choose genre (Pop, Rock, Jazz, etc.)",
            "Generate and wait for your song"
        ],
        tipsTh: ["ใช้โหมด Custom และใส่เนื้อร้องเองจะได้เพลงตรงใจมากกว่า"],
        tipsEn: ["Using Custom mode with your own lyrics gives better results"]
    },
    {
        id: "dance",
        titleTh: "สร้างวิดีโอเต้น",
        titleEn: "AI Dance Video",
        descriptionTh: "สร้างวิดีโอเต้นจากรูปภาพ ใส่ท่าเต้นให้ตัวละครเคลื่อนไหว",
        descriptionEn: "Create dance videos from images with character movement",
        icon: <Disc3 className="h-6 w-6" />,
        link: "/generate/motion",
        color: "from-fuchsia-500 to-purple-500",
        credits: "80",
        stepsTh: [
            "อัพโหลดรูปภาพตัวละคร (เห็นตัวเต็ม ยืนตรง)",
            "เลือกท่าเต้นจากคลังที่มีให้",
            "กดสร้างและรอผลลัพธ์ (3-5 นาที)"
        ],
        stepsEn: [
            "Upload character image (full body, standing straight)",
            "Select dance move from the library",
            "Generate and wait for results (3-5 minutes)"
        ],
        tipsTh: ["รูปคนควรยืนตรง เห็นแขนขาชัดเจน", "พื้นหลังควรเรียบ ไม่รกมาก"],
        tipsEn: ["Person should stand straight with arms/legs visible", "Background should be clean"]
    },
    {
        id: "transform",
        titleTh: "สร้างคลิป Time Lapse",
        titleEn: "Build House Time Lapse",
        descriptionTh: "สร้างวิดีโอเปลี่ยนฉากจากรูปเริ่มต้นไปยังรูปสิ้นสุด",
        descriptionEn: "Generate transition videos from start to end images",
        icon: <Wand2 className="h-6 w-6" />,
        link: "/generate/transform",
        color: "from-cyan-500 to-blue-500",
        credits: "60",
        stepsTh: [
            "อัพโหลดรูปเริ่มต้น (Start) เช่น ที่ดินว่าง",
            "อัพโหลดรูปสิ้นสุด (End) เช่น บ้านเสร็จ",
            "กดสร้างและรอวิดีโอที่แสดงการเปลี่ยนแปลง"
        ],
        stepsEn: [
            "Upload start image (e.g., empty land)",
            "Upload end image (e.g., finished house)",
            "Generate and wait for transition video"
        ]
    },
    {
        id: "photo",
        titleTh: "สร้างรูปภาพ AI",
        titleEn: "AI Image Generation",
        descriptionTh: "สร้างรูปภาพด้วย GPT Image, Seedream 4.5, Nano Banana",
        descriptionEn: "Generate images with GPT Image, Seedream 4.5, Nano Banana",
        icon: <ImageIcon className="h-6 w-6" />,
        link: "/generate/photo",
        color: "from-rose-500 to-red-500",
        credits: "4",
        stepsTh: [
            "เลือกโมเดลที่ต้องการ (Seedream, GPT Image, Nano)",
            "พิมพ์คำอธิบาย (Prompt) รูปภาพที่ต้องการ",
            "เลือกขนาดและจำนวนรูป",
            "กดสร้างและรอรับรูป"
        ],
        stepsEn: [
            "Select model (Seedream, GPT Image, Nano)",
            "Type image description (Prompt)",
            "Choose size and number of images",
            "Generate and wait for images"
        ]
    },
    {
        id: "cover",
        titleTh: "สร้างภาพปก",
        titleEn: "Cover Generator",
        descriptionTh: "สร้างภาพปก YouTube / Facebook ด้วย AI พร้อมเทมเพลต",
        descriptionEn: "Generate YouTube / Facebook covers with AI templates",
        icon: <Youtube className="h-6 w-6" />,
        link: "/generate/cover",
        color: "from-red-500 to-pink-500",
        credits: "4",
        stepsTh: [
            "เลือกเทมเพลตที่ต้องการ (YouTube, Facebook)",
            "อัพโหลดรูปหรือใช้ AI สร้างรูป",
            "แก้ไขข้อความ หัวข้อ",
            "ดาวน์โหลดภาพปกพร้อมใช้"
        ],
        stepsEn: [
            "Select template (YouTube, Facebook)",
            "Upload image or use AI to generate",
            "Edit text and title",
            "Download ready-to-use cover"
        ]
    },
    {
        id: "text-to-speech",
        titleTh: "สร้างเสียงพูด AI",
        titleEn: "AI Text to Speech",
        descriptionTh: "สร้างเสียงพูดสมจริงด้วย ElevenLabs รองรับหลายภาษา",
        descriptionEn: "Generate realistic speech with ElevenLabs, multi-language",
        icon: <Mic className="h-6 w-6" />,
        link: "/generate/text-to-speech",
        color: "from-violet-500 to-purple-600",
        credits: "14/1K ตัวอักษร",
        stepsTh: [
            "เลือกเสียงที่ต้องการ (กดปุ่มฟังตัวอย่างได้)",
            "พิมพ์ข้อความที่ต้องการแปลงเป็นเสียง",
            "เลือกภาษา และปรับ Stability",
            "กดสร้างและรอรับไฟล์เสียง"
        ],
        stepsEn: [
            "Select voice (click to preview)",
            "Type text to convert to speech",
            "Choose language and adjust Stability",
            "Generate and wait for audio file"
        ]
    },
    {
        id: "text-to-dialogue",
        titleTh: "บทสนทนา 2 คน AI",
        titleEn: "AI Two-Person Dialogue",
        descriptionTh: "สร้างบทสนทนาระหว่าง 2 คน พร้อมอารมณ์และจังหวะที่เป็นธรรมชาติ",
        descriptionEn: "Create conversations between 2 speakers with emotions",
        icon: <Users className="h-6 w-6" />,
        link: "/generate/text-to-dialogue",
        color: "from-pink-500 to-purple-500",
        credits: "14/1K ตัวอักษร",
        stepsTh: [
            "เลือกเสียงสำหรับผู้พูดคนที่ 1 และ 2",
            "พิมพ์บทสนทนา สลับกันระหว่างคน",
            "ใส่อารมณ์ได้ เช่น [excitedly] [whispering]",
            "กดสร้างและรอรับไฟล์เสียงบทสนทนา"
        ],
        stepsEn: [
            "Select voice for Speaker 1 and 2",
            "Type dialogue lines alternating between speakers",
            "Add emotions like [excitedly] [whispering]",
            "Generate and wait for dialogue audio"
        ],
        tipsTh: ["ใช้ [excitedly] สำหรับตื่นเต้น, [whispering] สำหรับกระซิบ, [laughing] สำหรับหัวเราะ"],
        tipsEn: ["Use [excitedly] for excitement, [whispering] for whisper, [laughing] for laugh"]
    },
    {
        id: "ai-chat",
        titleTh: "AI Chat",
        titleEn: "AI Chat",
        descriptionTh: "สนทนากับ Gemini AI วิเคราะห์รูปภาพ และ Clone รูป/วิดีโอ",
        descriptionEn: "Chat with Gemini AI, analyze images, clone images/videos",
        icon: <MessageSquare className="h-6 w-6" />,
        link: "/ai-chat",
        color: "from-teal-500 to-cyan-500",
        credits: "ฟรี",
        stepsTh: [
            "เลือกแท็บที่ต้องการ: Chat, Clone IMG, Clone Video",
            "พิมพ์คำถามหรืออัพโหลดรูปเพื่อวิเคราะห์",
            "AI จะตอบกลับพร้อมข้อมูลที่เป็นประโยชน์"
        ],
        stepsEn: [
            "Select tab: Chat, Clone IMG, Clone Video",
            "Type question or upload image for analysis",
            "AI will respond with helpful information"
        ]
    },
    {
        id: "prompt-ai",
        titleTh: "Prompt AI Assistant",
        titleEn: "Prompt AI Assistant",
        descriptionTh: "ผู้ช่วยส่วนตัวช่วยคิด Prompt และเตรียมสคริปต์",
        descriptionEn: "Personal assistant for prompts and scripts",
        icon: <Bot className="h-6 w-6" />,
        link: "/prompt-ai",
        color: "from-indigo-600 to-blue-700",
        credits: "4",
        stepsTh: [
            "เลือกประเภทความช่วยเหลือที่ต้องการ",
            "พิมพ์ไอเดียหรือหัวข้อของคุณ",
            "AI จะช่วยสร้าง Prompt หรือสคริปต์ให้"
        ],
        stepsEn: [
            "Select type of assistance needed",
            "Type your idea or topic",
            "AI will help create prompt or script"
        ]
    },
    {
        id: "watermark",
        titleTh: "สร้างลายน้ำ",
        titleEn: "Add Watermark",
        descriptionTh: "เพิ่มลายน้ำบนวิดีโอหรือรูปภาพตามต้องการ",
        descriptionEn: "Add watermark to videos or images",
        icon: <Stamp className="h-6 w-6" />,
        link: "/watermark",
        color: "from-slate-500 to-gray-600",
        credits: "5",
        stepsTh: [
            "วางลิงก์วิดีโอหรืออัพโหลดไฟล์",
            "พิมพ์ข้อความลายน้ำ หรืออัพโหลดโลโก้",
            "เลือกตำแหน่งและขนาดที่ต้องการ",
            "กดสร้างและดาวน์โหลด"
        ],
        stepsEn: [
            "Paste video link or upload file",
            "Type watermark text or upload logo",
            "Choose position and size",
            "Generate and download"
        ]
    },
    {
        id: "videobulk",
        titleTh: "สร้างวิดีโอแบบ Bulk",
        titleEn: "Bulk Video Generation",
        descriptionTh: "สร้างวิดีโอหลายชิ้นพร้อมกันจากหลาย Prompt",
        descriptionEn: "Generate multiple videos at once from multiple prompts",
        icon: <Layers className="h-6 w-6" />,
        link: "/generate/videobulk",
        color: "from-blue-500 to-indigo-500",
        credits: "30-60 ต่อวิดีโอ",
        stepsTh: [
            "พิมพ์ Prompt ทีละบรรทัด (1 บรรทัด = 1 วิดีโอ)",
            "เลือกโมเดลและอัตราส่วน",
            "กดสร้างทั้งหมด",
            "ระบบจะทำงานอัตโนมัติทีละชิ้น"
        ],
        stepsEn: [
            "Type prompts line by line (1 line = 1 video)",
            "Select model and aspect ratio",
            "Click generate all",
            "System will process automatically"
        ]
    },
    {
        id: "videomerge",
        titleTh: "รวมวิดีโอ AI",
        titleEn: "Video Merge",
        descriptionTh: "รวมวิดีโอ 2-5 ไฟล์เข้าด้วยกันเป็นไฟล์เดียว",
        descriptionEn: "Merge 2-5 video files into one",
        icon: <Layers className="h-6 w-6" />,
        link: "/generate/videomerge",
        color: "from-orange-500 to-red-500",
        credits: "10",
        stepsTh: [
            "วางลิงก์วิดีโอ 2-5 ไฟล์ที่ต้องการรวม",
            "ลาก-วางเพื่อเรียงลำดับ",
            "กดรวมวิดีโอ",
            "ดาวน์โหลดไฟล์เดียวที่รวมเสร็จ"
        ],
        stepsEn: [
            "Paste 2-5 video links to merge",
            "Drag and drop to reorder",
            "Click merge videos",
            "Download the merged file"
        ]
    },
    {
        id: "professional",
        titleTh: "Professional Mode",
        titleEn: "Professional Mode",
        descriptionTh: "สร้างวิดีโอระดับมืออาชีพด้วย Template และการตั้งค่าขั้นสูง",
        descriptionEn: "Create professional videos with templates and advanced settings",
        icon: <Crown className="h-6 w-6" />,
        link: "/professional",
        color: "from-yellow-500 to-amber-500",
        credits: "50+",
        stepsTh: [
            "เลือกเทมเพลตที่ต้องการ",
            "กรอกข้อมูลตามที่เทมเพลตกำหนด",
            "ปรับการตั้งค่าขั้นสูงตามต้องการ",
            "กดสร้างและรอรับผลงานมืออาชีพ"
        ],
        stepsEn: [
            "Select desired template",
            "Fill in template requirements",
            "Adjust advanced settings",
            "Generate and wait for professional output"
        ]
    }
];

const Manual = () => {
    const { t } = useLanguage();

    return (
        <div className="min-h-screen bg-background">
            <Navbar />
            <main className="pt-24 pb-16">
                <div className="container mx-auto px-4">
                    {/* Header */}
                    <header className="text-center mb-12">
                        <Badge variant="secondary" className="mb-4">
                            <Book className="h-3 w-3 mr-1" />
                            {t("คู่มือการใช้งาน", "User Guide")}
                        </Badge>
                        <h1 className="text-4xl md:text-5xl font-bold mb-4">
                            {t("คู่มือการใช้งาน", "How to Use")} <span className="text-gradient">GenCNX</span>
                        </h1>
                        <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
                            {t(
                                "เรียนรู้วิธีการใช้งานเครื่องมือ AI ทุกประเภท พร้อมขั้นตอนและเคล็ดลับสำหรับผลลัพธ์ที่ดีที่สุด",
                                "Learn how to use all AI tools with step-by-step guides and tips for best results."
                            )}
                        </p>
                    </header>

                    {/* Quick Start */}
                    <Card variant="glow" className="mb-12 max-w-4xl mx-auto">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <UserPlus className="h-5 w-5 text-primary" />
                                {t("เริ่มต้นใช้งาน", "Getting Started")}
                            </CardTitle>
                            <CardDescription>
                                {t("ขั้นตอนสำหรับผู้ใช้ใหม่", "Steps for new users")}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="grid md:grid-cols-3 gap-4">
                                <div className="flex items-start gap-3 p-4 rounded-xl bg-muted/30">
                                    <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-bold shrink-0">1</div>
                                    <div>
                                        <h4 className="font-medium">{t("สมัครสมาชิก", "Sign Up")}</h4>
                                        <p className="text-sm text-muted-foreground">{t("กดปุ่ม 'เริ่มต้นใช้งาน' และสร้างบัญชี", "Click 'Get Started' and create account")}</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3 p-4 rounded-xl bg-muted/30">
                                    <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-bold shrink-0">2</div>
                                    <div>
                                        <h4 className="font-medium">{t("เติมเครดิต", "Top Up Credits")}</h4>
                                        <p className="text-sm text-muted-foreground">{t("เลือกแพ็คเกจที่เหมาะกับการใช้งาน", "Choose a package that fits your needs")}</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3 p-4 rounded-xl bg-muted/30">
                                    <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-bold shrink-0">3</div>
                                    <div>
                                        <h4 className="font-medium">{t("เริ่มสร้างผลงาน", "Start Creating")}</h4>
                                        <p className="text-sm text-muted-foreground">{t("เลือกบริการและเริ่มสร้างสรรค์ได้เลย", "Select a service and start creating")}</p>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Service Guides */}
                    <h2 className="text-2xl font-bold mb-6 text-center">
                        {t("บริการทั้งหมด", "All Services")} ({serviceGuides.length})
                    </h2>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-7xl mx-auto">
                        {serviceGuides.map((service) => (
                            <Card key={service.id} variant="glass" className="overflow-hidden hover:border-primary/30 transition-all">
                                <CardHeader className="pb-3">
                                    <div className="flex items-start justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${service.color} flex items-center justify-center text-white`}>
                                                {service.icon}
                                            </div>
                                            <div>
                                                <CardTitle className="text-lg">
                                                    {t(service.titleTh, service.titleEn)}
                                                </CardTitle>
                                                <div className="flex items-center gap-2 mt-1">
                                                    <Badge variant="outline" className="text-xs">
                                                        <Coins className="h-3 w-3 mr-1" />
                                                        {service.credits}
                                                    </Badge>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <CardDescription className="mt-2">
                                        {t(service.descriptionTh, service.descriptionEn)}
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    {/* Steps */}
                                    <div className="space-y-2">
                                        <h4 className="text-sm font-medium text-muted-foreground">{t("ขั้นตอนการใช้งาน", "Steps")}</h4>
                                        {(t(service.stepsTh, service.stepsEn) as unknown as string[]).map((step, idx) => (
                                            <div key={idx} className="flex items-start gap-2 text-sm">
                                                <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                                                <span>{step}</span>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Tips */}
                                    {service.tipsTh && service.tipsEn && (
                                        <div className="p-3 rounded-lg bg-primary/5 border border-primary/10">
                                            <h4 className="text-sm font-medium text-primary mb-1">💡 {t("เคล็ดลับ", "Tips")}</h4>
                                            {(t(service.tipsTh, service.tipsEn) as unknown as string[]).map((tip, idx) => (
                                                <p key={idx} className="text-xs text-muted-foreground">• {tip}</p>
                                            ))}
                                        </div>
                                    )}

                                    {/* Link Button */}
                                    <Button asChild variant="outline" className="w-full group">
                                        <Link to={service.link}>
                                            {t("ไปหน้าบริการ", "Go to Service")}
                                            <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                                        </Link>
                                    </Button>
                                </CardContent>
                            </Card>
                        ))}
                    </div>

                    {/* Contact Section */}
                    <section className="mt-16 text-center max-w-4xl mx-auto p-12 rounded-3xl bg-primary/5 border border-primary/10">
                        <Sparkles className="h-10 w-10 text-primary mx-auto mb-4" />
                        <h2 className="text-2xl font-bold mb-4">{t("ต้องการความช่วยเหลือเพิ่มเติม?", "Need more help?")}</h2>
                        <p className="text-muted-foreground mb-8">
                            {t(
                                "หากคุณยังมีข้อสงสัยหรือพบปัญหาในการใช้งาน สามารถติดต่อทีมงานของเราได้ตลอดเวลา",
                                "If you still have questions or encounter issues, feel free to contact our team anytime."
                            )}
                        </p>
                        <div className="flex flex-wrap justify-center gap-4">
                            <Link to="/contact">
                                <Button variant="hero">{t("ติดต่อเรา", "Contact Us")}</Button>
                            </Link>
                            <Link to="/faq">
                                <Button variant="outline">{t("คำถามที่พบบ่อย", "FAQ")}</Button>
                            </Link>
                        </div>
                    </section>
                </div>
            </main>
            <Footer />
        </div>
    );
};

export default Manual;
